import { useFolderItemCounts } from "@/hooks/useFolderItemCounts";
import { useFolderWithItems } from "@/hooks/useFolderWithItems";
import { FolderItem, ItemType } from '../types';
import { sortFoldersAndItems } from '../utils/sorting';

interface UseFolderDataProps {
  gameId?: string;
  itemType?: ItemType;
  folders: FolderItem[];
  showItems?: boolean;
  isLoading?: boolean;
}

interface UseFolderDataReturn {
  finalFolders: FolderItem[];
  rootItemCount: number | undefined;
  sortedRootFolderWithItems: FolderItem | null | undefined;
  isLoadingCombined: boolean;
}

/**
 * Hook to manage folder data fetching and processing
 * Combines item counts and folder items based on showItems preference
 */
export const useFolderData = ({
  gameId,
  itemType = 'glossary',
  folders,
  showItems = true,
  isLoading = false,
}: UseFolderDataProps): UseFolderDataReturn => {
  // Use linkEngine to get folders with items or just counts
  const { 
    foldersWithItems, 
    isLoading: itemsLoading, 
    rootItemCount: rootItemCountFromItems, 
    rootFolderWithItems 
  } = useFolderWithItems({
    gameId,
    itemType,
    folders,
    showItems,
  });

  const { 
    foldersWithCounts, 
    isLoading: countsLoading, 
    rootItemCount: rootItemCountFromCounts 
  } = useFolderItemCounts({
    gameId,
    itemType,
    folders,
  });

  // Use the appropriate root item count based on showItems preference
  const rootItemCount = showItems ? rootItemCountFromItems : rootItemCountFromCounts;

  // Choose which data to use based on showItems preference
  const finalFolders = sortFoldersAndItems(showItems ? foldersWithItems : foldersWithCounts);
  
  // Sort root folder items if present
  const sortedRootFolderWithItems = rootFolderWithItems && rootFolderWithItems.children
    ? {
        ...rootFolderWithItems,
        children: rootFolderWithItems.children
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      }
    : rootFolderWithItems;
  
  // Combine loading states
  const isLoadingCombined = isLoading || (showItems ? itemsLoading : countsLoading);

  return {
    finalFolders,
    rootItemCount,
    sortedRootFolderWithItems,
    isLoadingCombined,
  };
};
